import { Navigate, Outlet } from "react-router-dom";
import {
  FolderOpen,
  HandCoins,
  IdCard,
  LayoutDashboard,
  LifeBuoy,
  Receipt,
  Rocket,
  ScrollText,
  Store,
  TimerReset,
  UserRound,
  Users,
} from "lucide-react";
import { AppShell, type NavMap } from "@/shared/ui/layout";
import { useAuth } from "@/features/auth/auth.context";
import { navForCountry } from "@/features/kyc/kycNav";

/**
 * ═══════════════════════════════════════════════════════════════════
 * El panel del SOCIO (Anahí, 27-ago) — el mismo armazón con su propio
 * mapa: resumen, pedidos, red, comisiones, recursos y KYC.
 *
 * ── Quién entra ──────────────────────────────────────────────────────
 * CUALQUIER sesión. Un cliente que aún no es socio también tiene aquí
 * su casa: ve sus pedidos, su invitación a la red y el camino a socio —
 * cerrarle la puerta hasta "ser socio" sería esconderle exactamente lo
 * que lo vuelve uno. Lo que sí distingue el resumen es SU condición.
 *
 * El franquiciatario tiene ADEMÁS `/mi-negocio` (su tienda); comparte
 * con este panel las páginas de `features/panel/`.
 * ═══════════════════════════════════════════════════════════════════
 */
const SOCIO_NAV: NavMap = [
  {
    label: "Mi cuenta",
    items: [
      { to: "/socio", label: "Resumen", icon: LayoutDashboard },
      { to: "/socio/pedidos", label: "Mis pedidos", icon: Receipt },
      { to: "/socio/reloj", label: "Reloj de recompra", icon: TimerReset },
      { to: "/socio/red", label: "Mi red", icon: Users },
      { to: "/socio/comisiones", label: "Mis comisiones", icon: HandCoins },
      { to: "/socio/recursos", label: "Recursos", icon: FolderOpen },
      { to: "/socio/kyc", label: "KYC", icon: IdCard },
      { to: "/socio/perfil", label: "Mi perfil", icon: UserRound },
    ],
  },
  {
    label: "Legal y soporte",
    note: "Viven fuera del panel y traen su propio cromo.",
    external: true,
    items: [
      { to: "/shop", label: "Tienda It's The Best", icon: Store },
      { to: "/soporte", label: "Soporte", icon: LifeBuoy },
      { to: "/legal", label: "Legal", icon: ScrollText },
    ],
  },
  /*
   * SIEMPRE al final (Anahí, 27-ago): la puerta a la franquicia, con el
   * mismo trato visual que las secciones externas del panel de la casa.
   */
  {
    label: "Adquiere tu franquicia.",
    note: "Tu tienda propia, precios de mayorista y los bonos de franquicia.",
    external: true,
    items: [{ to: "/franquicias", label: "Conocer la franquicia", icon: Rocket }],
  },
];

export function SocioShell() {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: "/socio" }} />;
  }

  return (
    <AppShell nav={navForCountry(SOCIO_NAV, user?.country)}>
      <Outlet />
    </AppShell>
  );
}
